import { Option, Report, SaveReportData, UpdateReportData } from '@/types';
import client from './client';

async function list({
  lastReportId,
  limit,
  title,
  types,
  search,
}: {
  lastReportId?: string;
  limit?: number;
  title?: string;
  types?: string;
  search?: string;
}) {
  const { data } = await client.get(`/reports`, {
    params: {
      ...(lastReportId ? { lastReportId } : {}),
      ...(limit ? { limit } : {}),
      ...(title ? { title } : {}),
      ...(types ? { types } : {}),
      ...(search ? { search } : {}),
    },
  });
  return data;
}

async function create({ reportData }: { reportData: SaveReportData }) {
  const { data } = await client.post('/reports', { data: reportData });

  return data;
}

async function remove({
  flowId,
  type,
  isSingle,
}: {
  flowId: string;
  type: string;
  isSingle: boolean;
}) {
  const { data } = await client.delete(`/reports`, {
    params: { id: flowId, type, isSingle },
  });

  return data;
}

async function get({ id, type }: { id: string; type?: string }) {
  const { data } = await client.get(`/report`, {
    params: { id, type },
  });
  const { answer, ...rest } = data;
  const returnData: Report = {
    output: {
      answer: data.answer,
    },
    ...rest,
  };
  return returnData;
}

async function getByFlow({
  flowId,
  types,
}: {
  flowId: string;
  types: string[];
}): Promise<Record<string, Report | null>> {
  const { data } = await client.get(`/reports-by-flow`, {
    params: {
      flowId,
      types: types.join(','),
    },
  });

  const reportsMap: Record<string, any> = data?.reports || {};
  const normalized: Record<string, Report | null> = {};

  for (const type of types) {
    const raw = reportsMap[type];
    if (!raw) {
      normalized[type] = null;
      continue;
    }

    normalized[type] = {
      output: {
        answer: raw.answer,
      },
      ...raw,
    } as Report;
  }

  return normalized;
}

async function updateTitle({
  title,
  flowId,
  id,
  type,
}: {
  title: string;
  flowId: string | null;
  id: string | null;
  type: string | null;
}) {
  const { data } = await client.patch(`/report`, {
    title,
    flowId,
    id,
    type,
  });
  return data;
}

async function update({ reportData }: { reportData: UpdateReportData }) {
  const { data } = await client.put('/report', { data: reportData });

  return data;
}

async function getReportTypes({ userId }: { userId: string }) {
  const { data } = await client.get<string[]>(`/report-types`, {
    params: { userId },
  });
  return data;
}

async function getUniqueTitle({ title }: { title: string }) {
  const { data } = await client.get<string>(`/reports-unique-title`, {
    params: { title },
  });
  return data;
}

async function getRtagsOptions({ ptalRefId }: { ptalRefId: string }) {
  const { data } = await client.get<Option[]>(`/reports-by-ptalref`, {
    params: { ptalRefId },
  });
  return data;
}

async function checkReportTypes({ types, flowId }: { types: string[]; flowId: string }) {
  const { data } = await client.get<Record<string, boolean>>(`/check-report-types`, {
    params: { types: types.join(','), flowId },
  });
  return data;
}

export const reports = {
  get,
  getByFlow,
  list,
  remove,
  updateTitle,
  getReportTypes,
  getUniqueTitle,
  update,
  create,
  getRtagsOptions,
  checkReportTypes,
};
